import { useEffect, useState } from "react";
import { NavigateFunction } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import { toast } from "@/hooks/use-toast";
import {
  enrichStale,
  getServicesHealth,
  ServiceCheck,
} from "@/services/adminApi";
import { DeadLettersSection } from "../components/DeadLettersSection";

const STALE_ENTITIES: { key: string; label: string }[] = [
  { key: "candidates", label: "Candidats" },
  { key: "companies", label: "Entreprises" },
  { key: "schools", label: "Écoles" },
];

const SERVICE_LABELS: Record<string, string> = {
  db: "PostgreSQL",
  database: "PostgreSQL",
  redis: "Redis",
  elasticsearch: "Elasticsearch",
  es: "Elasticsearch",
  keycloak: "Keycloak",
  n8n: "n8n",
  stripe: "Stripe",
};

function defaultBeforeDate(): string {
  const d = new Date(Date.now() - 30 * 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

// Opérations bulk : ré-enrichir les entités périmées (remède direct à la famine
// entreprise/école) + santé des services. Surface des endpoints déjà
// implémentés côté backoffice mais jamais exposés en UI.
export function OperationsTab({ navigate }: { navigate: NavigateFunction }) {
  const [before, setBefore] = useState(defaultBeforeDate());
  const [busy, setBusy] = useState<string | null>(null);
  const [health, setHealth] = useState<Record<string, ServiceCheck> | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState(false);

  const loadHealth = async () => {
    setHealthLoading(true);
    setHealthError(false);
    try {
      const r = await getServicesHealth(navigate);
      setHealth(r.services);
    } catch {
      setHealth(null);
      setHealthError(true);
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    void loadHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runEnrichStale(entity: string, label: string) {
    const ok = await confirmDialog({
      title: `Ré-enrichir les ${label.toLowerCase()}`,
      description: `Met en file d'enrichissement toutes les entités « ${label} » non mises à jour depuis le ${before}.`,
      confirmLabel: "Mettre en file",
    });
    if (!ok) return;
    setBusy(entity);
    try {
      const res = await enrichStale(navigate, entity, before);
      toast({
        title: "Entités mises en file",
        description: `${res.queued.toLocaleString("fr-FR")} ${label.toLowerCase()} enfilé(e)s.`,
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Échec",
        description: (e as Error)?.message ?? "Erreur inconnue",
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Ré-enrichir les entités périmées</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Non mises à jour depuis le</Label>
            <Input type="date" value={before} onChange={(e) => setBefore(e.target.value)} className="w-[200px]" />
          </div>
          <div className="flex flex-wrap gap-2">
            {STALE_ENTITIES.map((e) => (
              <Button
                key={e.key}
                variant="outline"
                size="sm"
                disabled={busy !== null || !before}
                onClick={() => runEnrichStale(e.key, e.label)}
              >
                {busy === e.key ? "Mise en file..." : e.label}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Réenfile les entités dont l'enrichissement est ancien. Utile pour résorber un backlog
            entreprise/école une fois la flotte à jour.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium">Santé des services</CardTitle>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={loadHealth} title="Rafraîchir">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {healthLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Chargement...</div>
          ) : healthError ? (
            <div className="py-6 text-center text-sm text-destructive">
              Erreur de chargement — cliquer sur ⟳ pour réessayer
            </div>
          ) : !health ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Indisponible
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(health).map(([name, check]) => (
                <div
                  key={name}
                  className="flex items-center justify-between rounded-md border p-2"
                >
                  <span className="text-sm">{SERVICE_LABELS[name] ?? name}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {Math.round(check.latency_ms)} ms
                    </span>
                    <Badge variant={check.status === "ok" ? "secondary" : "destructive"}>
                      {check.status === "ok" ? "OK" : "Erreur"}
                    </Badge>
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <DeadLettersSection navigate={navigate} />
    </div>
  );
}
